/**
 * The Director panel: a prompt, and Claude working the stage through the
 * API's own MCP tools, on the Worker, with this tab's key.
 *
 * Every step streams in as it happens and is drawn as it arrives: the
 * summarised reasoning (note), the call card forming as the model writes it,
 * the call itself (REST, curl, MCP), its answer, any call the server denied
 * before making it, token use, and the stop. Stop aborts at once. Worlds and
 * hands dropped on the prompt become its scope; creating Worlds is off unless
 * ticked. A finished run can be saved as a use case, which the Gallery replays
 * without any AI. While the Director is asleep (switched off, or no Anthropic
 * key to run on) the panel says so and the prompt searches the Gallery
 * instead; with no key of the deployment's and none of the user's, it asks for
 * the user's own (stage/anthropic-key.ts), which wakes it.
 *
 * The panel states the residual exposure plainly: which Anthropic key a run
 * spends (the user's goes to Anthropic through this deployment for that run
 * only), the prompt and the World data it reads go to Anthropic, and the
 * schedule-bit key does not.
 */

import { h, mount as put } from '../../../console/src/dom.ts';
import { globalName } from '../../../console/src/names.ts';
import type {
  Args, AssistConfig, AssistEvent, AssistKeySource, AssistMonth, AssistRequest, CallRecord, Held, Json, PresenceIn, Screen, Target, Unsub, UseCase,
} from '../contracts.ts';
import { ANTHROPIC_KEY, keyControl, keyLine, keyTail, type KeyUse } from './anthropic-key.ts';
import { carrying, clickDrop, keepFocus, magnetic, verbCovers, watchDrops } from '../ui/panel.ts';
import { casesFor, requestGallery, searchCases } from '../usecases/families.ts';
import { copyButton, twinTabs, twinTexts } from './inspector.ts';
import { knownSecrets, pretty, redactJson, redactText } from './wire-log.ts';
import { modalOpen, reducedMotion } from '../ui/a11y.ts';
import { toUseCase } from '../usecases/recorder.ts';
import { hex, readSlots } from '../model/masks.ts';
import { isObj } from '../model/json.ts';

// -- the run, folded from its events ------------------------------------------------------------------------

export interface CallItem {
  readonly kind: 'call';
  readonly step: number;
  readonly callId: string;
  readonly tool: string;
  readonly args: Args;
  readonly rest: { readonly method: string; readonly path: string; readonly query?: string };
  readonly mcp: Json;
  readonly status: number | null;
  readonly ms: number | null;
  readonly head: string | null;
  readonly commitSeq: number | null;
  readonly body: Json | undefined;
  readonly truncated: boolean;
}
export type Item =
  | { readonly kind: 'note'; readonly text: string }
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'forming'; readonly callId: string; readonly tool: string; readonly partial: string }
  | { readonly kind: 'denied'; readonly step: number; readonly callId: string; readonly tool: string; readonly why: string; readonly detail: string }
  | CallItem;

export interface Tally { readonly turns: number; readonly input: number; readonly output: number; readonly cacheRead: number; readonly cacheWrite: number }

export interface Run {
  readonly status: 'idle' | 'opening' | 'running' | 'done' | 'problem';
  readonly assistId: string | null;
  readonly model: string | null;
  readonly mode: 'act' | 'look' | null;
  readonly items: readonly Item[];
  readonly usage: Tally;
  readonly stop: Extract<AssistEvent, { type: 'stop' }> | null;
  readonly problem: { readonly type: string; readonly title: string; readonly status: number; readonly detail: string } | null;
  readonly month: AssistMonth | null;
  /** Whose Anthropic key the run spends, as its `open` said; null until then, or from a server that does not say. */
  readonly key: AssistKeySource | null;
}

export const emptyRun = (): Run => ({
  status: 'idle', assistId: null, model: null, mode: null, items: [],
  usage: { turns: 0, input: 0, output: 0, cacheRead: 0, cacheWrite: 0 }, stop: null, problem: null, month: null, key: null,
});

const replaceAt = <T>(list: readonly T[], i: number, item: T): T[] => list.map((x, j) => (j === i ? item : x));

/** One event onto the run. Pure; the panel folds every event it is sent, in order. */
export const foldAssist = (run: Run, ev: AssistEvent): Run => {
  const items = run.items;
  switch (ev.type) {
    case 'open':
      return { ...run, status: 'running', assistId: ev.assistId, model: ev.model, mode: ev.mode, month: ev.month, key: ev.key ?? null };
    case 'note':
    case 'text': {
      const last = items.at(-1);
      if (last && last.kind === ev.type) return { ...run, status: 'running', items: replaceAt(items, items.length - 1, { kind: ev.type, text: last.text + ev.delta }) };
      return { ...run, status: 'running', items: [...items, { kind: ev.type, text: ev.delta }] };
    }
    case 'forming': {
      const i = items.findIndex((x) => x.kind === 'forming' && x.callId === ev.callId);
      const item: Item = { kind: 'forming', callId: ev.callId, tool: ev.tool, partial: ev.partial };
      return { ...run, items: i >= 0 ? replaceAt(items, i, item) : [...items, item] };
    }
    case 'call': {
      const item: CallItem = {
        kind: 'call', step: ev.step, callId: ev.callId, tool: ev.tool, args: ev.arguments, rest: ev.rest, mcp: ev.mcp,
        status: null, ms: null, head: null, commitSeq: null, body: undefined, truncated: false,
      };
      const i = items.findIndex((x) => x.kind === 'forming' && x.callId === ev.callId);
      return { ...run, items: i >= 0 ? replaceAt(items, i, item) : [...items, item] };
    }
    case 'result': {
      const i = items.findIndex((x) => x.kind === 'call' && x.callId === ev.callId);
      if (i < 0) return run;
      const was = items[i] as CallItem;
      return { ...run, items: replaceAt(items, i, { ...was, status: ev.status, ms: ev.ms, head: ev.head, commitSeq: ev.commitSeq, body: ev.body, truncated: ev.truncated }) };
    }
    case 'denied': {
      const item: Item = { kind: 'denied', step: ev.step, callId: ev.callId, tool: ev.tool, why: ev.why, detail: ev.detail };
      const i = items.findIndex((x) => x.kind === 'forming' && x.callId === ev.callId);
      return { ...run, items: i >= 0 ? replaceAt(items, i, item) : [...items, item] };
    }
    case 'usage':
      return {
        ...run,
        usage: {
          turns: Math.max(run.usage.turns, ev.turn),
          input: run.usage.input + ev.input, output: run.usage.output + ev.output,
          cacheRead: run.usage.cacheRead + ev.cacheRead, cacheWrite: run.usage.cacheWrite + ev.cacheWrite,
        },
      };
    case 'stop':
      return { ...run, status: run.status === 'problem' ? 'problem' : 'done', stop: ev, month: ev.month, items: items.filter((x) => x.kind !== 'forming') };
    case 'problem':
      return { ...run, status: 'problem', problem: ev.problem };
  }
};

export const calls = (run: Run): CallItem[] => run.items.filter((x): x is CallItem => x.kind === 'call');
export const replyOf = (run: Run): string => run.items.filter((x) => x.kind === 'text').map((x) => (x as { text: string }).text).join('').trim();

export const kfmt = (n: number): string => (n >= 1000 ? `${(Math.round(n / 100) / 10).toString()}k` : String(n));

/** Whose key a run spent, in a few words. */
export const KEY_WORDS: Record<AssistKeySource, string> = { own: 'on your key', deployment: 'on the deployment’s key', scripted: 'scripted: no key spent' };

/** "stop end_turn · 5 turns · 9 calls · 41k in (34k cached) · 6.2k out · on your key" */
export const stopLine = (run: Run): string => {
  if (!run.stop) return '';
  const u = run.usage;
  const input = u.input + u.cacheRead + u.cacheWrite;
  const denied = run.items.filter((x) => x.kind === 'denied').length;
  return [
    `stop ${run.stop.reason}`,
    `${run.stop.turns} turn${run.stop.turns === 1 ? '' : 's'}`,
    `${run.stop.calls} call${run.stop.calls === 1 ? '' : 's'}`,
    ...(denied ? [`${denied} denied`] : []),
    `${kfmt(input)} in${u.cacheRead ? ` (${kfmt(u.cacheRead)} cached)` : ''}`,
    `${kfmt(u.output)} out`,
    ...(run.key ? [KEY_WORDS[run.key]] : []),
  ].join(' · ');
};

export const DENIED_WHY: Record<string, string> = {
  'not-offered': 'not a Director tool',
  'outside-scope': 'outside the scope you gave it',
  'look-mode': 'look mode only reads',
  'create-cap': 'creating Worlds is off, or its cap is spent',
  budget: 'the call budget is spent',
  'invalid-arguments': 'the arguments do not fit the tool',
};

export const STOP_WHY: Record<string, string> = {
  end_turn: 'finished', max_turns: 'reached its turn limit', max_calls: 'reached its call limit', budget: 'spent its token budget',
  deadline: 'ran out of time', refusal: 'declined', max_tokens: 'ran out of room mid-answer', aborted: 'stopped', error: 'failed',
};

// -- config: awake or asleep ---------------------------------------------------------------------------------

export interface ConfigView {
  readonly awake: boolean;
  readonly model: string | null;
  readonly line: string;
  readonly modes: readonly ('look' | 'act')[];
  readonly month: string | null;
  readonly disclosure: string;
  /** A deployment's scripted stand-in in the model's place: it plays a fixed look-around and reads no prompt. */
  readonly scripted?: boolean;
  /** Whose Anthropic key the next run would spend; 'none' when there is none to run on. */
  readonly key: KeyUse;
  /** Asleep only for want of a key: the user's own wakes it. */
  readonly needsKey?: boolean;
  /** The deployment has a key of its own (or plays a script), so a run needs none of the user's. */
  readonly deployment: boolean;
}

export const DISCLOSURE = 'Your prompt and the World data it reads are sent to Anthropic. Your schedule-bit key is not.';
/** Asleep for want of any key: the deployment has none, and the user has not added theirs. */
export const ADD_KEY = 'Add your Anthropic key to wake the Director.';
export const RESIDUAL = 'Relay asks and delegates it makes can write proposed planes into other workspaces’ Worlds that granted ask.';
export const SCRIPTED = 'Scripted stand-in: this deployment has no model, so it plays a fixed look around (it reads your Worlds and changes nothing) whatever you ask. Your prompt is not sent anywhere. The Gallery plays real use cases.';

/**
 * The server's disclosure without its sentence on whose Anthropic key a run spends: where the
 * dock says that for this user (`keyLine`), once is enough. The rest is kept as the server wrote it.
 */
export const withoutKeySentence = (text: string): string =>
  text.split(/(?<=[.!?])\s+/).filter((sentence) => !/\bAnthropic key\b/i.test(sentence)).join(' ');

/** The disclosure once: the server's already names the relay-ask exposure, so the residual line is added only when it does not. */
export const disclosureOf = (v: Pick<ConfigView, 'disclosure' | 'scripted'>): string =>
  v.scripted ? SCRIPTED : /\b(ask|delegate)\b/i.test(v.disclosure) ? v.disclosure : `${v.disclosure} ${RESIDUAL}`;

const isConfig = (x: unknown): x is AssistConfig => isObj(x) && typeof (x as { available?: unknown }).available === 'boolean';

/**
 * Awake or asleep, and on whose key. `own` is the user's Anthropic key, when they entered one. A
 * server that says nothing of `keys` predates own keys: available means the deployment's key. The
 * dev script wins over any key; the user's own over the deployment's, whose day or month may be
 * spent (`reason`) where the user's still runs.
 */
export const configView = (cfg: AssistConfig | { problem: Json } | null, own: string | null = null): ConfigView => {
  const base = { key: 'none' as KeyUse, deployment: false };
  if (cfg === null) return { ...base, awake: false, model: null, line: 'Checking whether the Director is offered here…', modes: [], month: null, disclosure: DISCLOSURE };
  if (!isConfig(cfg)) {
    const p = isObj(cfg.problem) ? cfg.problem : {};
    const status = typeof p.status === 'number' ? p.status : 0;
    const detail = typeof p.detail === 'string' ? p.detail : 'the assistant could not be reached';
    return {
      ...base, awake: false, model: null, modes: [], month: null, disclosure: DISCLOSURE,
      line: status === 501 ? 'The Director is not offered by this deployment (501). The Gallery works without it.' : `The Director sleeps here: ${detail}${status ? ` (${status})` : ''}. Type to search the Gallery.`,
    };
  }
  const month = `${cfg.month.used}/${cfg.month.limit} this month`;
  const deployment = cfg.keys ? cfg.keys.deployment : cfg.available;
  const disclosure = cfg.disclosure || DISCLOSURE;
  if (!cfg.available) {
    return { ...base, deployment, awake: false, model: cfg.model, modes: cfg.modes, month, disclosure, line: `The Director sleeps here: ${cfg.reason ?? 'it is switched off'} (503). Type to search the Gallery.` };
  }
  if (cfg.model === 'scripted') {
    // The script wins over any key and is counted like the deployment's, so a spent day or month
    // stops it, and the user's own key would not help.
    if (cfg.reason !== null) {
      return { key: 'scripted', deployment: true, awake: false, scripted: true, model: 'scripted stand-in', modes: cfg.modes, month, disclosure: SCRIPTED, line: `The Director sleeps here: ${cfg.reason} (503). Type to search the Gallery.` };
    }
    return { key: 'scripted', deployment: true, awake: true, scripted: true, model: 'scripted stand-in', modes: cfg.modes, month, disclosure: SCRIPTED, line: `a scripted stand-in (no model here) · ${month}` };
  }
  const model = cfg.model ?? 'the Director';
  if (own && cfg.keys?.own) {
    return { key: 'own', deployment, awake: true, model: cfg.model, modes: cfg.modes, month, disclosure, line: `${model} is awake on your key ${keyTail(own)}` };
  }
  if (!cfg.keys || (deployment && cfg.reason === null)) {
    return { key: 'deployment', deployment, awake: true, model: cfg.model, modes: cfg.modes, month, disclosure, line: `${model} is awake · ${month}` };
  }
  // Available, but not on the deployment's key (none, or its day or month spent): the user's own wakes it.
  return {
    key: 'none', deployment, needsKey: true, awake: false, model: cfg.model, modes: cfg.modes, month, disclosure,
    line: deployment && cfg.reason ? `${ADD_KEY} On the deployment’s key: ${cfg.reason}.` : ADD_KEY,
  };
};

// -- scope and the request -------------------------------------------------------------------------------------

/** What a dropped thing adds to the scope: a World of this workspace, or another workspace's World as a hand. */
export const scopeFrom = (held: Held, ownWorkspace: string | null): { world?: string; hand?: string } | null => {
  const d = isObj(held.data) ? held.data : {};
  const global = typeof d.global === 'string' ? d.global : typeof d.globalName === 'string' ? d.globalName : null;
  const world = held.world ?? (typeof d.world === 'string' ? d.world : null) ?? (held.kind === 'port' ? held.label : null);
  const name = global ?? world;
  if (!name) return null;
  const slash = name.indexOf('/');
  if (slash < 0) return { world: name };
  const ws = name.slice(0, slash);
  const rest = name.slice(slash + 1);
  return ownWorkspace !== null && ws === ownWorkspace ? { world: rest } : { hand: name };
};

export interface Draft {
  readonly prompt: string;
  readonly mode: 'act' | 'look';
  readonly worlds: readonly string[];
  readonly hands: readonly string[];
  readonly allowCreate: boolean;
}

export const requestOf = (d: Draft, today: string, focus: { world?: string; date?: string; hour?: number }, history: readonly { prompt: string; reply: string }[]): AssistRequest => ({
  prompt: d.prompt.trim().slice(0, 2000),
  mode: d.mode,
  ...(d.worlds.length ? { worlds: [...d.worlds].slice(0, 16) } : {}),
  ...(d.hands.length ? { hands: [...d.hands].slice(0, 16) } : {}),
  ...(d.allowCreate ? { allowCreate: true } : {}),
  today,
  ...(focus.world || focus.date ? { focus: { ...(focus.world ? { world: focus.world } : {}), ...(focus.date ? { date: focus.date } : {}), ...(focus.hour === undefined ? {} : { hour: focus.hour }) } } : {}),
  ...(history.length ? { history: history.slice(-4).map((x) => ({ prompt: x.prompt.slice(0, 2000), reply: x.reply.slice(0, 2000) })) } : {}),
});

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A finished run as a use case the Gallery can replay with no AI: its calls, in order, expecting
 * the statuses they got, made the way the recorder makes one (usecases/recorder.ts toUseCase),
 * so what would collide on a second run becomes a variable by the same rules: Worlds ensured,
 * relay names fresh per run, days kept apart, soft-hold deadlines from now, tokens and ids saved
 * from the answer that gave them. `records` are the calls the Worker made, as the Client was told
 * of them: their answers are whole where a result event may be clipped, and they say when each
 * went out.
 */
export const useCaseFromRun = (run: Run, prompt: string, title: string, records: ReadonlyMap<string, CallRecord> = new Map()): UseCase => {
  const made = calls(run).filter((c) => c.status !== null && c.status !== 0);
  const assistId = run.assistId ?? '';
  const asRecord = (c: CallItem): CallRecord => {
    const told = records.get(c.callId);
    return {
      id: c.callId, op: c.tool, args: c.args, persona: 'A',
      source: { kind: 'director', assistId, step: c.step, callId: c.callId }, origin: null, via: 'rest',
      twins: { rest: { method: c.rest.method, url: c.rest.path, headers: {} }, curl: '', mcp: null },
      sentAt: told?.sentAt ?? Date.now(),
      answer: { status: c.status ?? 0, body: told?.answer?.body ?? c.body ?? null, head: c.head, ms: c.ms ?? 0, requestId: null, rate: null },
    };
  };
  const uc = toUseCase(made.map(asRecord), title.trim() || prompt.trim().slice(0, 48) || 'Director run');
  return {
    ...uc,
    id: `director-${run.assistId ?? Date.now().toString(16)}`,
    blurb: prompt.trim().slice(0, 280),
    origin: 'director',
    // The ghost hand says which tool each step is.
    steps: made.length === 0 ? [] : uc.steps.map((s) => ({ ...s, say: s.op })),
  };
};

// -- the ghost of a forming call -----------------------------------------------------------------------------

/** Where a call aims on the stage: its World's bits when the day and slots are named, else the World. */
export const aimOf = (args: Json | undefined): Target | null => {
  if (!isObj(args) || typeof args.w !== 'string' || !args.w) return null;
  const w = args.w;
  const body = isObj(args.body) ? args.body : {};
  const date = typeof body.date === 'string' && DAY.test(body.date) ? body.date : null;
  const mask = readSlots(body.slots);
  const slots = mask ? hex(mask) : null;
  if (date && slots) {
    return { kind: 'bits', id: `bits:${w}:${date}`, world: w, data: { w, date, ...(typeof body.hour === 'number' ? { hour: body.hour } : {}), slots } };
  }
  return { kind: 'field', id: `field:${w}`, world: w, data: { w } };
};

/** The same, read from the arguments as the model is still writing them: only values already closed count. */
export const aimOfPartial = (partial: string): Target | null => {
  const w = /"w"\s*:\s*"([^"\\]+)"/.exec(partial)?.[1];
  if (!w) return null;
  const date = /"date"\s*:\s*"(\d{4}-\d{2}-\d{2})"/.exec(partial)?.[1];
  const list = /"slots"\s*:\s*(\[[\d,\s]*\])/.exec(partial)?.[1];
  const hex = /"slots"\s*:\s*"(0x[0-9a-fA-F]+)"/.exec(partial)?.[1];
  let slots: Json | undefined;
  try { slots = list ? (JSON.parse(list) as Json) : hex; } catch { slots = undefined; }
  return aimOf({ w, body: { ...(date ? { date } : {}), ...(slots === undefined ? {} : { slots }) } });
};

/**
 * The intent cue a streamed event earns: one per call, as soon as where it aims is known. A
 * tool that names a World waits for the World to be written (or for the call itself); one
 * that names none is cued at its first forming.
 */
export const cueOf = (
  ev: AssistEvent, cued: ReadonlySet<string>, takesWorld: (tool: string) => boolean,
): { readonly callId: string; readonly tool: string; readonly target: Target | null } | null => {
  if (ev.type !== 'forming' && ev.type !== 'call') return null;
  if (cued.has(ev.callId)) return null;
  const target = ev.type === 'forming' ? aimOfPartial(ev.partial) : aimOf(ev.arguments as Json);
  if (ev.type === 'forming' && target === null && takesWorld(ev.tool)) return null;
  return { callId: ev.callId, tool: ev.tool, target };
};

/** What a screen reader hears of a call's answer: the step, the tool, the verdict and its reason. */
export const resultWords = (ev: Extract<AssistEvent, { type: 'result' }>): string => {
  const detail = isObj(ev.body) && typeof ev.body.detail === 'string' ? `: ${ev.body.detail}` : '';
  const verdict = ev.status === 0 ? 'unreachable' : ev.status < 300 ? `${ev.status}` : ev.status === 409 ? `refused (409)` : `${ev.status}`;
  return `Director step ${ev.step}, ${ev.tool}: ${verdict}${ev.status >= 300 ? detail : ''}`;
};

/** Presence the asking tab sends so peers see "Maya's Director is working". */
export const presenceOf = (ev: AssistEvent): PresenceIn | null => {
  switch (ev.type) {
    case 'open': return { t: 'director', ph: 'start' };
    case 'call': return { t: 'director', ph: 'step', step: ev.step, tool: ev.tool };
    case 'stop': return { t: 'director', ph: 'done' };
    default: return null;
  }
};

const curlOf = (c: CallItem): string => {
  const body = isObj(c.args.body) ? JSON.stringify(c.args.body) : null;
  return [`curl -sS -X ${c.rest.method} "$SB_API${c.rest.path}${c.rest.query ? `?${c.rest.query}` : ''}"`,
    '  -H "authorization: Bearer $SB_KEY"',
    ...(body ? ['  -H "content-type: application/json"', `  -d '${body.replace(/'/g, "'\\''")}'`] : [])].join(' \\\n');
};

/**
 * What Esc means to the Director when it reaches the page (the prompt keeps its own Esc: stop, close the log, let
 * go). A run stops only from inside the Director. The log beside the stage closes from inside it (its own button
 * has focus once clicked) or from anywhere that is not typing: it is the topmost thing open over the stage.
 */
export const escapeFrom = (s: { readonly running: boolean; readonly log: boolean; readonly inside: boolean; readonly typingElsewhere: boolean }): 'stop' | 'close-log' | null => {
  if (s.running && s.inside) return 'stop';
  if (s.log && (s.inside || !s.typingElsewhere)) return 'close-log';
  return null;
};

// -- the screen -------------------------------------------------------------------------------------------------

const TRY =['what is still free on my Worlds tomorrow?', 'set up a clinic day with a waitlist', 'race me for 10:00'];

/** What of a handed-over request the dock keeps as its draft: mode, scope and whether it may create. */
export const draftFrom = (req: AssistRequest): Partial<Pick<Draft, 'mode' | 'worlds' | 'hands' | 'allowCreate'>> => ({
  ...(req.mode === 'look' || req.mode === 'act' ? { mode: req.mode } : {}),
  ...(Array.isArray(req.worlds) ? { worlds: req.worlds.filter((x) => typeof x === 'string') } : {}),
  ...(Array.isArray(req.hands) ? { hands: req.hands.filter((x) => typeof x === 'string') } : {}),
  ...(typeof req.allowCreate === 'boolean' ? { allowCreate: req.allowCreate } : {}),
});

export const mount: Screen = (root, ctx) => {
  const doc = root.ownerDocument;
  const offs: Unsub[] = [];
  let cfg: AssistConfig | { problem: Json } | null = null;
  let run: Run = emptyRun();
  let lastPrompt = '';
  let history: { prompt: string; reply: string }[] = [];
  let controller: AbortController | null = null;
  let draft: Draft = { prompt: '', mode: 'act', worlds: [], hands: [], allowCreate: false };
  let saveTitle = '';
  /** Prompts sent, newest last: ↑ in an empty prompt walks back through them. */
  let asked: string[] = [];
  let askedAt = -1;
  let notice = '';
  let queued = false;
  const records = new Map<string, CallRecord>();
  const open = new Set<string>();

  const head = h('header', { class: 'dir-head' });
  const scope = h('div', { class: 'dir-scope' });
  // Redrawn whole on every streamed event, so it is not a live region: the verdicts, denials and the stop are announced one by one.
  const timeline = h('ol', { class: 'dir-timeline', 'aria-label': 'Director steps' });
  // The strip: what the Director says now and its calls as chips, a line or two above the pill; the full log opens beside the stage.
  const say = h('p', { class: 'dir-say' });
  const chips = h('ol', { class: 'dir-chips', 'aria-label': 'Director calls' });
  const logBtn = h('button', { class: 'dir-link dir-logbtn', type: 'button', 'aria-expanded': 'false', title: 'the whole run: notes, calls with REST, curl and MCP, answers' }, 'log ▸');
  const strip = h('div', { class: 'dir-strip' }, say, h('div', { class: 'dir-chiprow' }, chips, logBtn));
  const tries = h('div', { class: 'dir-tries', role: 'group', 'aria-label': 'Try' });
  const orb = h('button', { class: 'dir-orb', type: 'button', title: 'assistConfig', 'aria-label': 'Director orb: check whether the Director is offered here (or drop the Lens on it)' }, '◈');
  const cued = new Set<string>();
  const foot = h('footer', { class: 'dir-foot' });
  const asleep = h('div', { class: 'dir-asleep' });
  const disclosure = h('p', { class: 'dir-disclosure' });
  const prompt = h('textarea', { class: 'dir-input', rows: '1', maxlength: '2000', placeholder: 'Ask the Director: set up a clinic day, race me for 10:00…', 'aria-label': 'Director prompt (/ or ⌘/)' });
  const runBtn = h('button', { class: 'dir-run', type: 'button', 'aria-label': 'run (Enter)' }, '▶');
  const stopBtn = h('button', { class: 'dir-stop', type: 'button', 'aria-label': 'stop (Esc)' }, '■');
  const grip = h('button', { class: 'deck-grip', type: 'button', 'aria-label': 'carry the prompt card onto the stage' }, '⠿');
  const promptBox = h('div', { class: 'dir-prompt' }, orb, prompt, runBtn, stopBtn, grip);
  // The command bar: a slim pill at rest; open while focused; live while a run streams, and a moment after, then it settles.
  const section = h('section', { class: 'dir', 'aria-label': 'Director', 'data-state': 'rest' });
  let settling = false;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let hovered = false;
  const setState = (): void => {
    const inside = section.contains(doc.activeElement);
    // Focused, the bar is open (mode, create, the tries) even while a finished run settles; only a running one is live.
    section.dataset.state = running() ? 'live' : inside ? 'open' : settling ? 'live' : 'rest';
  };
  const settleSoon = (): void => {
    clearTimeout(settleTimer);
    settling = true;
    // It settles on its own: only a hand resting on it holds it up; focus in the prompt keeps the compact open bar.
    settleTimer = setTimeout(function settle() {
      if (hovered) { settleTimer = setTimeout(settle, 1500); return; }
      settling = false;
      setState();
    }, 6000);
  };
  const setLog = (on: boolean): void => {
    section.toggleAttribute('data-log', on);
    logBtn.setAttribute('aria-expanded', String(on));
    logBtn.textContent = on ? 'log ◂' : 'log ▸';
    if (on) timeline.scrollTop = timeline.scrollHeight;
  };
  logBtn.addEventListener('click', () => setLog(!section.hasAttribute('data-log')));
  section.addEventListener('focusin', setState);
  section.addEventListener('focusout', () => setTimeout(setState, 0));
  section.addEventListener('pointerenter', () => { hovered = true; });
  section.addEventListener('pointerleave', () => { hovered = false; });
  // While the hand carries something the pill at rest tucks to its orb, clear of the drop it is aiming.
  offs.push(ctx.gestures.onState((s) => {
    section.toggleAttribute('data-tuck', s.phase === 'dragging' || s.phase === 'carrying');
  }));

  const workspace = (): string | null => {
    const me = ctx.records.get(null, 'me');
    return isObj(me) && typeof me.workspace === 'string' ? me.workspace : null;
  };
  /** A key handed over with a request (the Deck's assist card): for that run only, never kept. */
  let handed: string | null = null;
  const own = (): string | null => handed ?? ctx.session.anthropicKey();
  const view = (): ConfigView => configView(cfg, own());
  // The user's own Anthropic key: a chip above the prompt, and the box that adds, checks, replaces and removes it.
  const keyCtl = keyControl(ctx, { deployment: () => view().deployment, scripted: () => view().scripted === true });
  let keyAsked = false;
  /** Does this tool name a World (a `w`)? Then its ghost waits until the World is written. */
  const takesWorld = (tool: string): boolean => ctx.catalogue().byId(tool)?.holes.some((x) => x.name === 'w') ?? true;
  const running = (): boolean => controller !== null;

  const schedule = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; drawRun(); });
  };

  const drawHead = (): void => {
    const v = view();
    const modes = v.modes.length ? v.modes : (['look', 'act'] as const);
    const create = h('input', { type: 'checkbox', checked: draft.allowCreate, 'aria-label': 'allow creating Worlds (at most 3)' });
    create.addEventListener('change', () => { draft = { ...draft, allowCreate: create.checked }; });
    orb.className = `dir-orb${v.awake ? ' awake' : ''}${running() ? ' working' : ''}`;
    put(head,
      h('span', { class: 'dir-eyebrow' }, 'DIRECTOR'),
      h('span', { class: 'dir-model', ...(v.scripted ? { title: SCRIPTED } : {}) }, v.model ?? ''),
      h('span', { class: 'dir-modes', role: 'radiogroup', 'aria-label': 'mode' }, ...modes.map((m) => h('button', {
        class: 'dir-mode', type: 'button', role: 'radio', 'aria-checked': String(draft.mode === m),
        title: m === 'look' ? 'look: reads only; works with a read key' : 'act: may write; needs the write scope',
        onclick: () => { draft = { ...draft, mode: m }; drawHead(); },
      }, m))),
      h('label', { class: 'dir-create' }, create, ' create Worlds'),
      h('span', { class: 'dir-month' }, run.month ? `${run.month.used}/${run.month.limit} this month` : (v.month ?? '')),
      h('button', { class: 'dir-link', type: 'button', title: 'Lens: check again', onclick: () => void check() }, '⌕'));
    runBtn.disabled = running() || !v.awake;
    stopBtn.disabled = !running();
    promptBox.classList.toggle('asleep', !v.awake);
    prompt.placeholder = v.awake ? 'Ask the Director: set up a clinic day, race me for 10:00…'
      : v.needsKey ? `${ADD_KEY.slice(0, -1)} · or search the Gallery` : 'Search the Gallery: clinic, relay, race…';
    // Which key a run spends comes first: the rest of the disclosure is the same whichever it is.
    disclosure.textContent = v.awake || v.needsKey ? `${keyLine(v.key, own())} ${disclosureOf({ ...v, disclosure: withoutKeySentence(v.disclosure) })}` : disclosureOf(v);
    keyCtl.paint();
  };

  const drawScope = (): void => {
    const chip = (label: string, kind: 'world' | 'hand', remove: () => void): HTMLElement => h('span', { class: `dir-scope-chip k-${kind}` },
      h('span', {}, kind === 'hand' ? `hand ${label}` : label),
      h('button', { class: 'dir-x', type: 'button', 'aria-label': `remove ${label} from the scope`, onclick: () => { remove(); drawScope(); } }, '×'));
    const add = h('input', { class: 'dir-scope-add', type: 'text', autocapitalize: 'none', placeholder: '+ World', 'aria-label': 'add a World (or workspace/world) to the scope' });
    add.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      // Typed in any case: the scope holds the name the API knows.
      const named = globalName(add.value);
      if (!named) return;
      const s = scopeFrom({ kind: 'port', id: named, label: named, data: {} }, workspace());
      if (s?.world && !draft.worlds.includes(s.world)) draft = { ...draft, worlds: [...draft.worlds, s.world] };
      if (s?.hand && !draft.hands.includes(s.hand)) draft = { ...draft, hands: [...draft.hands, s.hand] };
      drawScope();
    });
    const focus = ctx.focus().world;
    put(scope,
      h('span', { class: 'dir-label' }, 'scope'),
      ...draft.worlds.map((w) => chip(w, 'world', () => { draft = { ...draft, worlds: draft.worlds.filter((x) => x !== w) }; })),
      ...draft.hands.map((w) => chip(w, 'hand', () => { draft = { ...draft, hands: draft.hands.filter((x) => x !== w) }; })),
      draft.worlds.length + draft.hands.length === 0 ? h('span', { class: 'dir-hint' }, 'any World of this workspace · drop Worlds or hands on the prompt to narrow it') : null,
      focus && !draft.worlds.includes(focus) ? h('button', { class: 'dir-link', type: 'button', onclick: () => { draft = { ...draft, worlds: [...draft.worlds, focus] }; drawScope(); } }, `+ ${focus}`) : null,
      add);
  };

  const itemEl = (it: Item): HTMLElement => {
    const known = knownSecrets(ctx);
    switch (it.kind) {
      case 'note': return h('li', { class: 'dir-item note' }, h('span', { class: 'dir-tag' }, 'note'), h('span', { class: 'dir-text' }, redactText(it.text, known)));
      case 'text': return h('li', { class: 'dir-item text' }, h('span', { class: 'dir-tag' }, 'text'), h('span', { class: 'dir-text' }, redactText(it.text, known)));
      case 'forming': return h('li', { class: 'dir-item forming' }, h('span', { class: 'dir-tag' }, 'forming…'), h('span', { class: 'dir-tool' }, it.tool), h('code', { class: 'dir-partial' }, redactText(it.partial.slice(-240), known)));
      case 'denied': return h('li', { class: 'dir-item denied', role: 'note' }, h('span', { class: 'dir-tag' }, '⊘ denied'), h('span', { class: 'dir-tool' }, it.tool),
        h('span', { class: 'dir-why' }, `${DENIED_WHY[it.why] ?? it.why} · ${redactText(it.detail, known)}`));
      case 'call': {
        const status = it.status === null ? '…' : String(it.status);
        const tone = it.status === null ? 'pending' : it.status < 300 ? 'ok' : it.status === 409 ? 'refused' : 'error';
        const expanded = open.has(it.callId);
        const detail = isObj(it.body) && typeof it.body.detail === 'string' ? it.body.detail : '';
        const record = records.get(it.callId);
        let tabs: HTMLElement | null = null;
        if (expanded) {
          let rest = `${it.rest.method} ${it.rest.path}${it.rest.query ? `?${it.rest.query}` : ''}${isObj(it.args.body) ? `\n\n${pretty(redactJson(it.args.body, known))}` : ''}`;
          let curl = curlOf(it);
          try {
            const t = twinTexts(ctx.client.twins(it.tool, it.args), known);
            rest = t.rest;
            curl = t.curl;
          } catch { /* the event's own rest line stands */ }
          tabs = twinTabs([
            { key: 'rest', label: 'REST', text: rest },
            { key: 'curl', label: 'curl', text: redactText(curl, known) },
            { key: 'mcp', label: 'MCP', text: pretty(redactJson(it.mcp, known)) },
            { key: 'answer', label: 'answer', text: it.status === null ? '(at the door…)' : `${it.status} · ${it.ms ?? 0} ms${it.head ? ` · head ${it.head.slice(0, 8)}…` : ''}${it.truncated ? ' · clipped' : ''}\n\n${pretty(redactJson(it.body ?? null, known))}` },
          ], it.status !== null && it.status >= 300 ? 'answer' : 'rest');
        }
        return h('li', { class: `dir-item call t-${tone}` },
          h('button', {
            class: 'dir-call', type: 'button', 'aria-expanded': String(expanded),
            onclick: () => { if (expanded) open.delete(it.callId); else open.add(it.callId); if (record) ctx.inspect({ kind: 'call', record }, { open: true }); drawRun(); },
          },
          h('span', { class: 'dir-step' }, String(it.step)),
          h('span', { class: 'dir-tool' }, it.tool),
          h('span', { class: 'dir-path' }, `${it.rest.method} ${it.rest.path}`),
          h('span', { class: 'dir-status' }, status),
          it.commitSeq !== null ? h('span', { class: 'dir-seq' }, `#${it.commitSeq}`) : null,
          detail ? h('span', { class: 'dir-detail' }, redactText(detail, known)) : null),
          tabs);
      }
    }
  };

  /** The strip: the Director's latest words on one line, and each call as a chip (its step, tool and answer). */
  const drawStrip = (): void => {
    const known = knownSecrets(ctx);
    const words = [...run.items].reverse().find((x) => x.kind === 'text' || x.kind === 'note');
    const forming = run.items.find((x) => x.kind === 'forming');
    say.textContent = words && (words.kind === 'text' || words.kind === 'note') ? redactText(words.text, known)
      : forming && forming.kind === 'forming' ? `forming ${forming.tool}…` : running() ? 'thinking…' : '';
    say.classList.toggle('is-note', words?.kind === 'note');
    keepFocus(chips, () => put(chips, ...run.items.flatMap((it): HTMLElement[] => {
      if (it.kind === 'call') {
        const tone = it.status === null ? 'pending' : it.status < 300 ? 'ok' : it.status === 409 ? 'refused' : 'error';
        const record = records.get(it.callId);
        const w = typeof it.args.w === 'string' ? it.args.w : null;
        return [h('li', { class: `dir-chip t-${tone}`, 'data-call': it.callId },
          h('button', {
            type: 'button', title: `${it.rest.method} ${it.rest.path}${w ? ` · ${w}` : ''}`,
            onclick: () => { if (record) ctx.inspect({ kind: 'call', record }, { open: true }); else setLog(true); },
          }, h('b', {}, String(it.step)), ` ${it.tool} `, h('span', { class: 'dir-chip-st' }, it.status === null ? '…' : String(it.status))))];
      }
      if (it.kind === 'denied') return [h('li', { class: 'dir-chip t-denied' }, h('span', {}, `⊘ ${it.tool}`))];
      if (it.kind === 'forming') return [h('li', { class: 'dir-chip t-forming' }, h('span', {}, `${it.tool}…`))];
      return [];
    })));
    chips.scrollLeft = chips.scrollWidth;
    section.toggleAttribute('data-ran', run.items.length > 0);
  };

  /** A call card leaves the strip and flies to the World it acts on (or up, when that World is not on screen). */
  const flyCall = (callId: string, w: string | null): void => {
    if (reducedMotion(doc) || typeof Element.prototype.animate !== 'function') return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const chip = [...chips.querySelectorAll<HTMLElement>('[data-call]')].find((el) => el.dataset.call === callId);
      const from = chip?.getBoundingClientRect();
      if (!chip || !from || from.width === 0) return;
      // To its World's ring (or field); a call that names none, to the middle of the stage's ground.
      const to = w ? ctx.fx.origins.at(`world:${w}`) : ctx.fx.origins.at('ground');
      const card = h('div', { class: `dir-fly ${chip.className}`, 'aria-hidden': 'true' }, chip.textContent ?? '');
      doc.body.append(card);
      const x0 = from.left;
      const y0 = from.top;
      const x1 = to ? to.x - from.width / 2 : x0;
      const y1 = to ? to.y - from.height / 2 : y0 - 200;
      const mx = (x0 + x1) / 2 + (x1 >= x0 ? -60 : 60);
      const my = Math.min(y0, y1) - 90;
      const at = (x: number, y: number, k: number) => `translate(${x.toFixed(0)}px, ${y.toFixed(0)}px) scale(${k})`;
      const run_ = card.animate([
        { transform: at(x0, y0, 1), opacity: 0.2, offset: 0 },
        { transform: at(x0, y0 - 10, 1.08), opacity: 1, offset: 0.12 },
        { transform: at(mx, my, 1), opacity: 1, offset: 0.55, easing: 'cubic-bezier(.3,0,.2,1)' },
        { transform: at(x1, y1, 0.7), opacity: 0.9, offset: 0.9 },
        { transform: at(x1, y1, 0.4), opacity: 0, offset: 1 },
      ], { duration: 820, easing: 'cubic-bezier(.45,0,.25,1)' });
      run_.onfinish = () => card.remove();
      run_.oncancel = () => card.remove();
    }));
  };

  const drawRun = (): void => {
    keepFocus(timeline, () => put(timeline, ...run.items.map(itemEl)));
    timeline.scrollTop = timeline.scrollHeight;
    drawStrip();
    const v = view();
    const can = run.status === 'done' && calls(run).some((c) => c.status !== null);
    const title = h('input', { class: 'dir-save-title', type: 'text', placeholder: 'name it', value: saveTitle, 'aria-label': 'use case title' });
    title.addEventListener('input', () => { saveTitle = title.value; });
    put(foot,
      run.problem ? h('p', { class: 'dir-problem', role: 'alert' }, `${run.problem.status} ${run.problem.title}: ${redactText(run.problem.detail, knownSecrets(ctx))}`) : null,
      run.stop ? h('p', { class: `dir-stopline r-${run.stop.reason}` }, stopLine(run), ` — ${STOP_WHY[run.stop.reason] ?? run.stop.reason}`,
        run.stop.refusal?.explanation ? ` (${run.stop.refusal.explanation})` : '') : null,
      running() ? h('p', { class: 'dir-working' }, `working · ${calls(run).length} call${calls(run).length === 1 ? '' : 's'}${run.key ? ` · ${KEY_WORDS[run.key]}` : ''}`) : null,
      can ? h('div', { class: 'dir-save' }, title, h('button', { class: 'dir-btn', type: 'button', onclick: save }, 'save as use case')) : null,
      notice ? h('p', { class: 'dir-notice' }, notice) : null,
      offerCases(v));
    if (!v.awake) {
      const matches = searchCases([...ctx.usecases.builtins(), ...ctx.usecases.mine()], prompt.value).slice(0, 6);
      // Asleep for want of a key: the key box opens (once; the user may close it), and the line leads there.
      if (v.needsKey && !keyAsked) { keyAsked = true; keyCtl.open(); }
      put(asleep, h('p', { class: 'dir-asleep-line' }, v.line),
        v.needsKey ? h('button', { class: 'dir-btn dir-addkey', type: 'button', onclick: () => keyCtl.open(true) }, 'add your Anthropic key ▸') : null,
        h('ul', { class: 'dir-matches' }, ...matches.map((uc) => h('li', {},
          h('button', {
            class: 'dir-match', type: 'button',
            onclick: () => requestGallery(ctx, { play: uc.id }),
          }, `▶ ${uc.title}`, h('small', {}, ` ${uc.steps.length} steps`))))),
        h('button', { class: 'dir-link', type: 'button', onclick: () => requestGallery(ctx, { search: prompt.value }) }, 'open the Gallery'));
      asleep.hidden = false;
    } else {
      asleep.hidden = true;
      put(tries, h('span', { class: 'dir-tag' }, 'try'),
        ...TRY.map((t) => h('button', { class: 'dir-try', type: 'button', onclick: () => { prompt.value = t; prompt.focus(); } }, t)));
    }
    drawHead();
    setState();
  };

  /**
   * A scripted stand-in only looks around, so after its run the Gallery's case for the prompt is offered: that
   * is where "set up a clinic day" is played for real, every call a real one.
   */
  const offerCases = (v: ConfigView): HTMLElement | null => {
    if (!v.scripted || running() || !lastPrompt) return null;
    const found = casesFor([...ctx.usecases.builtins(), ...ctx.usecases.mine()], lastPrompt);
    return h('div', { class: 'dir-cases' },
      h('p', {}, found.length ? 'This Director is a scripted stand-in: it looked around and changed nothing. The Gallery plays this for real:' : 'This Director is a scripted stand-in: it looked around and changed nothing. The Gallery plays real use cases.'),
      ...found.map((uc) => h('button', { class: 'dir-match', type: 'button', onclick: () => requestGallery(ctx, { play: uc.id }) }, `▶ ${uc.title}`, h('small', {}, ` ${uc.steps.length} steps`))),
      found.length ? null : h('button', { class: 'dir-link', type: 'button', onclick: () => requestGallery(ctx, { search: '' }) }, 'open the Gallery'));
  };

  const send = (frame: PresenceIn): void => {
    const worlds = new Set([...draft.worlds, ...(ctx.focus().world ? [ctx.focus().world as string] : [])]);
    for (const w of worlds) ctx.links.get(w)?.send(frame);
  };

  const check = async (): Promise<void> => {
    try {
      cfg = await ctx.director.config();
    } catch (e) {
      cfg = { problem: { status: 0, title: 'Unreachable', detail: e instanceof Error ? e.message : String(e), type: '' } };
    }
    if (isConfig(cfg) && cfg.modes.length && !cfg.modes.includes(draft.mode)) draft = { ...draft, mode: cfg.modes[0]! };
    drawRun();
  };

  const go = async (key: string | null = null): Promise<void> => {
    const text = prompt.value.trim();
    if (!text || running()) return;
    handed = key;
    if (!view().awake) { handed = null; drawRun(); return; }
    draft = { ...draft, prompt: text };
    lastPrompt = text;
    run = { ...emptyRun(), status: 'opening' };
    notice = '';
    saveTitle = '';
    open.clear();
    cued.clear();
    controller = new AbortController();
    drawRun();
    const plain = requestOf(draft, new Date().toISOString().slice(0, 10), ctx.focus(), history);
    // A handed key goes with this run; otherwise the Director's client adds the tab's own, if the user entered one.
    const req: AssistRequest = handed ? { ...plain, anthropicKey: handed } : plain;
    try {
      for await (const ev of ctx.director.run(req, controller.signal)) {
        run = foldAssist(run, ev);
        const frame = presenceOf(ev);
        if (frame) send(frame);
        // The call forming, as a ghost on the field: once per call, aimed as soon as its World is written.
        const cue = cueOf(ev, cued, takesWorld);
        if (cue) {
          cued.add(cue.callId);
          ctx.fx.conductor.play({ kind: 'intent', id: `director:${cue.callId}`, op: cue.tool, source: { kind: 'director', assistId: run.assistId ?? '' }, target: cue.target });
        }
        if (ev.type === 'call') flyCall(ev.callId, typeof ev.arguments.w === 'string' ? ev.arguments.w : null);
        if (ev.type === 'result') ctx.announce.polite(redactText(resultWords(ev), knownSecrets(ctx)));
        if (ev.type === 'denied') {
          ctx.announce.assertive(`Director denied ${ev.tool}: ${DENIED_WHY[ev.why] ?? ev.why}`);
          ctx.fx.conductor.play({ kind: 'withdraw', id: `director:${ev.callId}` });
        }
        if (ev.type === 'problem' && ev.problem.status === 503) void check();
        // Anthropic refused the user's key: the key control says so, open, where it can be checked, replaced or removed.
        if (ev.type === 'problem' && ev.problem.title === 'Assistant Key Rejected' && !handed) keyCtl.rejected(ev.problem.detail);
        schedule();
      }
    } catch (e) {
      if (!controller.signal.aborted) run = { ...run, status: 'problem', problem: { type: '', title: 'Stream broken', status: 0, detail: e instanceof Error ? e.message : String(e) } };
    } finally {
      if (controller.signal.aborted && !run.stop) run = { ...run, status: 'done', stop: { type: 'stop', reason: 'aborted', turns: run.usage.turns, calls: calls(run).length, month: run.month ?? { period: '', used: 0, limit: 0 } } };
      // A call that was forming when the run ended never flies: its ghost goes with the run.
      for (const id of cued) ctx.fx.conductor.play({ kind: 'withdraw', id: `director:${id}` });
      const reply = replyOf(run);
      if (reply) history = [...history, { prompt: text, reply }].slice(-4);
      handed = null;
      // The prompt empties for the next one; ↑ in an empty prompt brings this one back.
      asked = [...asked.filter((x) => x !== text), text].slice(-12);
      if (prompt.value.trim() === text) prompt.value = '';
      controller = null;
      settleSoon();
      send({ t: 'director', ph: 'done' });
      const said = reply ? ` It says: ${redactText(reply.slice(0, 240), knownSecrets(ctx))}${reply.length > 240 ? '…' : ''}` : '';
      ctx.announce.polite(run.stop ? `Director ${STOP_WHY[run.stop.reason] ?? run.stop.reason}: ${stopLine(run)}.${said}` : `Director ended.${said}`);
      drawRun();
    }
  };

  const stop = (): void => {
    controller?.abort();
    drawRun();
  };

  const save = (): void => {
    const uc = useCaseFromRun(run, lastPrompt, saveTitle, records);
    const vetted = ctx.vet(uc, ctx.catalogue());
    if (!vetted.ok) { notice = `Not saved: ${vetted.reasons.join('; ')}`; drawRun(); return; }
    ctx.usecases.save(uc);
    notice = `Saved “${uc.title}” to the Gallery: ${uc.steps.length} steps, replayable with no AI.`;
    drawRun();
  };

  runBtn.addEventListener('click', () => void go());
  stopBtn.addEventListener('click', stop);
  prompt.addEventListener('keydown', (e) => {
    if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && asked.length && (prompt.value === '' || asked.includes(prompt.value))) {
      e.preventDefault();
      const at = askedAt < 0 ? asked.length : askedAt;
      askedAt = Math.max(0, Math.min(asked.length, at + (e.key === 'ArrowUp' ? -1 : 1)));
      prompt.value = asked[askedAt] ?? '';
      return;
    }
    askedAt = -1;
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void go(); }
    else if (e.key === 'Escape' && running()) { e.preventDefault(); stop(); }
    else if (e.key === 'Escape' && section.hasAttribute('data-log')) { e.preventDefault(); setLog(false); }
    else if (e.key === 'Escape') { e.preventDefault(); prompt.blur(); settling = false; setState(); }
  });
  prompt.addEventListener('input', () => { if (!view().awake) drawRun(); });
  const onKey = (e: KeyboardEvent): void => {
    // A sheet that asked something keeps the keys until it is answered.
    if (modalOpen(doc)) return;
    const el = doc.activeElement;
    const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || (el as HTMLElement).isContentEditable);
    if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); prompt.focus(); prompt.select(); }
    // ⌘/ (Ctrl+/) opens the bar from anywhere, typing or not, and puts it away again.
    else if (e.key === '/' && (e.metaKey || e.ctrlKey) && !e.altKey) {
      e.preventDefault();
      if (section.contains(el)) { (el as HTMLElement).blur(); settling = false; setState(); } else prompt.focus();
    }
    else if (e.key === 'Escape') {
      const act = escapeFrom({ running: running(), log: section.hasAttribute('data-log'), inside: root.contains(el), typingElsewhere: typing && !root.contains(el) });
      if (act === 'stop') stop();
      // Once per press: a key the prompt, a sheet or a carry already took is theirs.
      else if (act === 'close-log' && !e.defaultPrevented && ctx.gestures.state().phase === 'idle') { e.preventDefault(); setLog(false); }
    }
  };
  doc.addEventListener('keydown', onKey);
  /** A prompt handed over (the first run, the Deck's assist card, a prompt card): its scope and mode land here, and with `run` it runs. */
  offs.push(ctx.bus.on('director', ({ request, run: now }) => {
    prompt.value = request.prompt;
    draft = { ...draft, ...draftFrom(request) };
    // A key that came with the request (the Deck's assist card) runs this once and is never kept.
    const key = typeof request.anthropicKey === 'string' && ANTHROPIC_KEY.test(request.anthropicKey) ? request.anthropicKey : null;
    drawScope();
    drawHead();
    if (now && configView(cfg, key ?? ctx.session.anthropicKey()).awake && !running()) void go(key);
    else prompt.focus();
    return true;
  }));

  // Worlds and hands dropped on the prompt narrow the scope (no API call: it is part of the next request).
  const addScope = (held: Held): void => {
    const s = scopeFrom(held, workspace());
    if (!s) { ctx.announce.assertive(`${held.label} is not a World or a hand`); return; }
    if (s.world && !draft.worlds.includes(s.world)) draft = { ...draft, worlds: [...draft.worlds, s.world] };
    if (s.hand && !draft.hands.includes(s.hand)) draft = { ...draft, hands: [...draft.hands, s.hand] };
    ctx.announce.polite(`scope: ${[...draft.worlds, ...draft.hands].join(', ')}`);
    drawScope();
  };
  const promptTarget: Target = { kind: 'prompt', id: 'director:prompt', data: {} };
  offs.push(ctx.gestures.target(promptBox, () => promptTarget, 'director'));
  offs.push(watchDrops(ctx.gestures, doc, (over, held) => over.id === 'director:prompt' && !verbCovers(ctx.gestures, held, over), addScope,
    // A World's port counts as a place to drop (it glows): the scope is where Worlds and hands go.
    (held) => (held.kind === 'port' ? `Add ${held.world ?? held.label} to the Director's scope` : null)));
  offs.push(clickDrop(promptBox, ctx.gestures, (held) => !verbCovers(ctx.gestures, held, promptTarget), addScope));

  // The orb is the Director's gate (§3.2 row 57): the Lens dropped on it asks again whether the Director is offered.
  const orbTarget: Target = { kind: 'gate:director', id: 'gate:director', data: {} };
  offs.push(ctx.gestures.target(orb, () => orbTarget, 'director'));
  const lensOnOrb = (held: Held, over: Target): boolean => held.kind === 'lens' && !verbCovers(ctx.gestures, held, over);
  offs.push(watchDrops(ctx.gestures, doc, (over, held) => over.id === 'gate:director' && lensOnOrb(held, over), () => void check(),
    () => 'Ask whether the Director is offered here (assistConfig)'));
  offs.push(clickDrop(orb, ctx.gestures, (held) => lensOnOrb(held, orbTarget), () => void check()));
  // Tucked to its orb (a carry, a case playing, a phone's World), the orb opens the bar; open, it asks again (assistConfig).
  // (Whether it was tucked is read at the press: the press's focus opens the bar before the click lands.)
  let tuckedAtPress = false;
  orb.addEventListener('pointerdown', () => { tuckedAtPress = getComputedStyle(prompt).display === 'none'; });
  orb.addEventListener('click', () => {
    if (carrying(ctx.gestures)) return;
    const tucked = tuckedAtPress || getComputedStyle(prompt).display === 'none';
    tuckedAtPress = false;
    if (tucked) { requestAnimationFrame(() => prompt.focus()); return; }
    void check();
  });
  // The Director's beats start at its orb (§4.4): a call it makes arcs from here to the World it acts on.
  offs.push(ctx.fx.origins.register('director', () => {
    const r = orb.getBoundingClientRect();
    return r.width === 0 && r.height === 0 ? null : { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }));
  offs.push(ctx.gestures.source(grip, () => ({ kind: 'prompt', id: 'director:prompt-card', label: prompt.value.slice(0, 40) || 'prompt', data: { prompt: prompt.value, mode: draft.mode } }), 'director'));
  offs.push(magnetic(promptBox));

  // The server's calls reach the Client as injected records; keep them by call id for the Inspector and the saved use case.
  // An assistConfig answer (the hand's Lens on the orb) is the Director's config too.
  offs.push(ctx.client.on((record, phase) => {
    if (record.source.kind === 'director') records.set(record.source.callId, record);
    const body = record.answer?.body;
    if (phase === 'answered' && record.op === 'assistConfig' && record.answer && record.answer.status < 300 && isConfig(body)) {
      cfg = body;
      drawRun();
    }
  }));
  offs.push(ctx.records.on((w, kind) => { if (w === null && kind === 'me') drawScope(); }));
  // The user's key added or removed wakes the Director or puts it to sleep.
  offs.push(ctx.session.on(() => { if (!running()) drawRun(); }));
  offs.push(() => keyCtl.off());
  // The World in view is offered as "+ world": it changes with the route.
  offs.push(ctx.router.on(() => drawScope()));

  keyCtl.el.classList.add('dir-key');
  put(section, timeline, strip, foot, head, keyCtl.el, tries, scope, asleep, promptBox, disclosure,
    h('div', { class: 'dir-copy' }, copyButton(() => replyOf(run), 'copy reply')));
  put(root, section);
  drawScope();
  drawRun();
  void check();

  return () => {
    controller?.abort();
    clearTimeout(settleTimer);
    doc.removeEventListener('keydown', onKey);
    offs.forEach((off) => off());
    root.replaceChildren();
  };
};
